<template>
  <Header/>
  <LoginStatus />
  <div class="container is-max-desktop">  
    <div v-if="booking.id != 0 && !isDeleted">
      <h1>Booking #{{ currentRoute.params.id }}</h1>
      <article v-if="isNew" class="message is-success">
        <div class="message-body">
          <p>Din booking er oprettet.</p>
          <p>Se den i <a :href="calendarBackHref">kalenderen</a>.</p>
        </div>
      </article>
      <article v-if="isUpdated" class="message is-success">
        <div class="message-body">
          <p>Din booking er opdateret.</p>
        </div>
      </article>
      <a
        v-if="!isNew && backHref"
        :href="backHref"
        class="button is-light booking-back"
      >
        <span class="icon">
          <Icon name="arrow-left" />
        </span>
        &nbsp;
        Tilbage
      </a>
      <table class="table is-striped is-fullwidth">
        <tbody>
          <tr>
            <th>Booking-status</th>
            <td>
              {{ global.phases[booking.phase].da_dk.short }}
              <p v-if="booking.endOriginal != '0000-00-00 00:00:00'">
                <span class="has-background-danger-light p-1 is-size-7"><em>Bemærk:</em> Afsluttet før tid</span>
              </p>
            </td>
          </tr>
          <tr>
            <th>Periode</th>
            <td>{{ formatPeriod(booking.start, booking.end) }}</td>
          </tr>
          <tr>
            <th>Køretøj</th>
            <td>
              {{ booking.vehicle.name }} ({{ booking.vehicle.brand }} {{ booking.vehicle.model }})
            </td>
          </tr>
          <tr v-if="showKmEditor" class="distance">
            <td colspan="2">
              <p class="label">Km-stande</p>
              <p v-if="isKmCorrectionEditing" class="km-stands__hint">
                Ret km-standene nedenfor. Ændringer notificerer administratorerne.
                <button type="button" class="km-unlock__btn" @click="cancelKmCorrection">
                  Annuller
                </button>
              </p>
              <p v-else class="km-stands__hint">Angiv begge <em>senest</em> ved aflevering.</p>
              <div class="km-stands">
                <div class="km-stands__row">
                  <div class="km-stands__field">
                    <div class="km-stands__field-head">
                      <label for="kmStandStart">Km ved afhentning</label>
                      <button
                        v-if="showStartKmInfo"
                        type="button"
                        class="km-info__btn"
                        :class="{ 'is-open': kmInfoOpen === 'start' }"
                        :aria-expanded="kmInfoOpen === 'start'"
                        aria-controls="km-info-tip"
                        aria-label="Info om km ved afhentning"
                        @click="toggleKmInfo('start')"
                      >
                        <Icon name="info" size="sm" />
                      </button>
                    </div>
                    <div class="control is-expanded has-icons-right">
                      <input
                        class="input"
                        ref="kmStandStart"
                        id="kmStandStart"
                        type="number"
                        inputmode="numeric"
                        min="0"
                        step="1"
                        autocomplete="off"
                        placeholder="Km ved afhentning"
                        v-model="kmStandStartInput"
                        :readonly="startKmLocked"
                        :class="{ 'is-locked': startKmLocked }"
                      >
                      <button
                        v-if="kmStandStartInput !== '' && !startKmLocked"
                        type="button"
                        class="km-reset-icon"
                        title="Nulstil"
                        aria-label="Nulstil start-km"
                        @click="resetKmStand('start')"
                      >
                        <Icon name="close" size="sm" />
                      </button>
                    </div>
                    <p v-if="startKmLocked" class="km-stands__lock-note">
                      Låst til forrige aflevering
                      <button type="button" class="km-unlock__btn" @click="unlockStartKm">
                        Afvig fra forrige
                      </button>
                    </p>
                    <p v-else-if="startKmDeviated" class="km-stands__lock-note km-stands__lock-note--warn">
                      Afviger fra forrige aflevering ({{ booking.vehicleLastKmStand }})
                      <button type="button" class="km-unlock__btn" @click="relockStartKm">
                        Brug forrige
                      </button>
                    </p>
                  </div>
                  <div class="km-stands__field">
                    <div class="km-stands__field-head">
                      <label for="kmStandEnd">Km ved aflevering</label>
                      <button
                        v-if="kmStandEndIsPrefill"
                        type="button"
                        class="km-info__btn"
                        :class="{ 'is-open': kmInfoOpen === 'end' }"
                        :aria-expanded="kmInfoOpen === 'end'"
                        aria-controls="km-info-tip"
                        aria-label="Info om forudfyldt km"
                        @click="toggleKmInfo('end')"
                      >
                        <Icon name="info" size="sm" />
                      </button>
                    </div>
                    <div class="control is-expanded has-icons-right">
                      <input
                        class="input"
                        ref="kmStandEnd"
                        id="kmStandEnd"
                        type="number"
                        inputmode="numeric"
                        min="0"
                        step="1"
                        autocomplete="off"
                        placeholder="Km ved aflevering"
                        v-model="kmStandEndInput"
                        @focus="onKmStandEndFocus"
                      >
                      <button
                        v-if="kmStandEndInput !== ''"
                        type="button"
                        class="km-reset-icon"
                        title="Nulstil"
                        aria-label="Nulstil slut-km"
                        @click="resetKmStand('end')"
                      >
                        <Icon name="close" size="sm" />
                      </button>
                    </div>
                  </div>
                  <div class="km-stands__action">
                    <button
                      ref="kmStandSaveBtn"
                      type="button"
                      class="button is-info"
                      :disabled="kmSaveSubmitting"
                      @click="saveKmStands()"
                    >
                      {{ buttons.kmSave }}
                    </button>
                  </div>
                </div>
                <p
                  v-if="liveDrivenKm !== null"
                  class="km-stands__live"
                  :class="{ 'km-stands__live--invalid': liveDrivenKm < 0 }"
                  aria-live="polite"
                >
                  Kørt: <strong>{{ liveDrivenKm }} km</strong>
                </p>
                <p v-if="kmInfoOpen === 'start' && showStartKmInfo" id="km-info-tip" class="km-info__tip" role="note">
                  Forrige kører har angivet <strong>{{ formatNumber(booking.vehicleLastKmStand) }}</strong> km som km-stand ved sin aflevering.
                </p>
                <p v-else-if="kmInfoOpen === 'end' && kmStandEndIsPrefill" id="km-info-tip" class="km-info__tip" role="note">
                  Forudfyldt med starten af start-km — tilføj de sidste cifre.
                </p>
                <div class="km-stands__messages">
                  <article v-if="kmStandWarning" class="message is-warning">
                    <div class="message-body">{{ kmStandWarning }}</div>
                  </article>
                  <article v-if="kmStandError" class="message is-danger">
                    <div class="message-body">{{ kmStandError }}</div>
                  </article>
                  <article v-if="messagesOnSaves.kmSaved.visible" class="message is-success">
                    <div class="message-body">
                      {{ kmSavedSuccessText }}
                    </div>
                  </article>
                </div>
              </div>
            </td>
          </tr>
          <tr v-else-if="booking.phase == 100">
            <th>Kørt</th>
            <td>
              {{ booking.kmStandEnd - booking.kmStandStart }} km <br><br>
              Start: {{ booking.kmStandStart }} km<br>
              Slut: {{ booking.kmStandEnd }} km
              <p v-if="canActivateKmCorrection" class="km-stands__closed-note">
                <button type="button" class="button is-small is-light" @click="activateKmCorrection">
                  Ret km-stande
                </button>
              </p>
              <p v-else-if="isAdmin" class="km-stands__closed-note">
                Administratorer retter km under <a href="/forbrug">Forbrug</a>.
              </p>
              <p v-else-if="isOwnAccount" class="km-stands__closed-note">
                Rettelsesvinduet er lukket. Kontakt en administrator, hvis tallene skal ændres.
              </p>
              <article v-if="messagesOnSaves.kmSaved.visible" class="message is-success mt-3">
                <div class="message-body">{{ kmSavedSuccessText }}</div>
              </article>
            </td>
          </tr>
          <tr v-if="booking.phase == 100">
            <th>Pris</th>
            <td>
              {{ parseInt(booking.timePriceV2) + parseInt(booking.distancePriceV2) }} kr.
            </td>
          </tr>
          <tr v-if="kmCorrectionsList.length">
            <th>Km-rettelser</th>
            <td>
              <ul class="km-corrections-list">
                <li v-for="correction in kmCorrectionsList" :key="correction.id">
                  {{ formatKmCorrection(correction) }}
                </li>
              </ul>
            </td>
          </tr>
          <tr>
            <th>Booket af</th>
            <td>{{ booking.createdBy.displayName }} ({{ booking.account.name }})</td>
          </tr>
          <tr>
            <th>Booket</th>
            <td>{{ formatDate(booking.created) }}</td>
          </tr>
          <tr v-if="booking.comment || booking.isAdminBooking">
            <th>Kommentar</th>
            <td>
              {{ booking.isAdminBooking ? 'Administrator-booking. ' : '' }}
              {{ booking.comment }}
            </td>
          </tr>
          <tr v-if="isOwnAccount" class="booking-share">
            <th>Deling</th>
            <td>
              <BookingSharePanel
                :booking="booking"
                :is-own-account="isOwnAccount"
                @share-updated="onShareUpdated"
              />
            </td>
          </tr>
        </tbody>
      </table>
      <div class="buttons">
        <router-link
          v-if="canEditBooking"
          class="button is-info"
          :to="'/booking/' + booking.id + '/ret'"
        >
          {{ booking.phase == 20 ? 'Ændr booking' : 'Forlæng booking' }}
        </router-link>
        <button
          v-if="
            global.user.account == booking.createdBy.account &&
            (booking.phase == 10 || booking.phase == 11) &&
            booking.endOriginal == '0000-00-00 00:00:00'
          "
          class="button is-warning"
          @click="bookingEnd()"
        >
          Afslut booking nu
        </button>
        <button
          v-if="global.user.account == booking.createdBy.account && booking.phase == 20"
          class="button is-danger"
          @click="bookingDelete()"
        >
          Slet booking
        </button>
      </div>
    </div>

    <div v-if="isDeleted">
      <h1>Booking #{{ currentRoute.params.id }} er slettet</h1>
      <article class="message is-success">
        <div class="message-body">
          <p>Bookingen er nu slettet.</p>
          <p>Gå til <a href="/kalender">kalenderen</a> for at booke en ny.</p>
        </div>
      </article>
    </div>
  </div>
  

  <Footer/>
</template>

<script>
import ax from 'axios'
export const axios = ax

import { defineComponent } from 'vue'
import { useComposition } from '@/compositions/composition'

import Header from '@/components/Header.vue'
import LoginStatus from '@/components/LoginStatus.vue'
import Footer from '@/components/Footer.vue'
import Icon from '@/components/Icon.vue'
import BookingSharePanel from '@/components/BookingSharePanel.vue'
import {
  assessKmStands,
  buildKmConfirmMessage,
  friendlyKmApiError,
  parseKmDigits
} from '@/booking/kmStands'
import { formatNumber, formatKmCorrection } from '@/forbrug/formatters'

export default defineComponent({
  setup() {
        const { global } = useComposition();
        return {
            global
        }
    },
  data() {
    return {
      booking: { 
        id: 0,
        vehicleLastKmStand: 0,
        canCorrectKm: false,
        kmCorrectionWindowOpen: false
      },
      isNew: false,
      isUpdated: false,
      isDeleted: false,
      buttons: {
        kmSave: 'Gem',
      },
      messagesOnSaves: {
        kmSaved: {
          visible: false
        },
      },
      kmStandError: '',
      kmStandWarning: '',
      kmStandStartInput: '',
      kmStandEndInput: '',
      kmStandEndPrefillPrefix: '',
      kmSaveSubmitting: false,
      kmInfoOpen: '',
      startKmUnlocked: false,
      kmCorrectionActive: false
    }
  },
  components: {
    Header,
    LoginStatus,
    Footer,
    Icon,
    BookingSharePanel
  },
  created() {
      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);
      this.isNew = urlParams.get('new') == '1' ? true : false;
      this.isUpdated = urlParams.get('updated') == '1' ? true : false;
      this.bookingLoad();
  },
  methods: {
    formatNumber,
    formatKmCorrection,
    toggleKmInfo(which) {
      this.kmInfoOpen = this.kmInfoOpen === which ? '' : which;
    },
    kmPrefillFromStart(startKm) {
      const start = parseInt(startKm, 10) || 0;
      if (start < 100) {
        return '';
      }
      return String(start).slice(0, -2);
    },
    applyEndKmPrefill() {
      if (parseInt(this.booking.kmStandEnd, 10) > 0) {
        this.kmStandEndInput = String(this.booking.kmStandEnd);
        this.kmStandEndPrefillPrefix = '';
        return;
      }

      const prefix = this.kmPrefillFromStart(this.effectiveStartKm);
      const previousPrefix = this.kmStandEndPrefillPrefix;
      // Only replace when empty or still just the previous prefill (user hasn't typed the last digits yet)
      if (this.kmStandEndInput === '' || this.kmStandEndInput === previousPrefix) {
        this.kmStandEndInput = prefix;
      }
      this.kmStandEndPrefillPrefix = prefix;
    },
    onKmStandEndFocus(e) {
      const el = e.target;
      this.$nextTick(() => {
        const len = el.value ? el.value.length : 0;
        if (typeof el.setSelectionRange === 'function') {
          el.setSelectionRange(len, len);
        }
      });
    },
    applyStartKmValue() {
      if (parseInt(this.booking.kmStandStart, 10) > 0) {
        this.kmStandStartInput = String(this.booking.kmStandStart);
        this.startKmUnlocked = false;
      } else if (parseInt(this.booking.vehicleLastKmStand, 10) > 0) {
        this.kmStandStartInput = String(this.booking.vehicleLastKmStand);
        this.startKmUnlocked = false;
      } else {
        this.kmStandStartInput = '';
        this.startKmUnlocked = true;
      }
    },
    unlockStartKm() {
      this.startKmUnlocked = true;
      this.kmInfoOpen = 'start';
      this.$nextTick(() => {
        if (this.$refs.kmStandStart) this.$refs.kmStandStart.focus();
      });
    },
    activateKmCorrection() {
      this.kmCorrectionActive = true;
      this.kmStandError = '';
      this.kmStandWarning = '';
      this.kmStandStartInput = String(this.booking.kmStandStart || '');
      this.kmStandEndInput = String(this.booking.kmStandEnd || '');
      this.kmStandEndPrefillPrefix = '';
      this.$nextTick(() => {
        if (this.$refs.kmStandEnd) this.$refs.kmStandEnd.focus();
      });
    },
    cancelKmCorrection() {
      this.kmCorrectionActive = false;
      this.kmStandError = '';
      this.kmStandWarning = '';
      this.kmStandStartInput = String(this.booking.kmStandStart || '');
      this.kmStandEndInput = String(this.booking.kmStandEnd || '');
      this.kmStandEndPrefillPrefix = '';
    },
    relockStartKm() {
      const last = parseInt(this.booking.vehicleLastKmStand, 10) || 0;
      if (last > 0) {
        this.kmStandStartInput = String(last);
        this.startKmUnlocked = false;
        this.applyEndKmPrefill();
      }
    },
    resetKmStand(which) {
      this.kmStandError = '';
      this.kmStandWarning = '';
      if (which === 'start') {
        if (this.startKmLocked) {
          return;
        }
        this.kmStandStartInput = '';
        this.startKmUnlocked = true;
        this.$nextTick(() => {
          if (this.$refs.kmStandStart) this.$refs.kmStandStart.focus();
        });
        return;
      }

      this.kmStandEndInput = '';
      this.kmStandEndPrefillPrefix = '';
      this.$nextTick(() => {
        if (this.$refs.kmStandEnd) this.$refs.kmStandEnd.focus();
      });
    },
    bookingLoad() {
      axios.get(process.env.VUE_APP_API_ROOT + '/booking.php?id=' + this.$route.params.id)
        .then(response => {
            this.booking = response.data;
            document.title = 'Booking nr. #' + this.booking.id;
            // After save/reload on closed booking, show read-only until user activates again
            if (this.booking.phase == 100) {
              this.kmCorrectionActive = false;
            }
            this.applyStartKmValue();
            if(this.booking.kmStandStart == 0) {
              this.vehicleGetLastKmStand(this.booking.vehicle.id);
            } else {
              this.applyEndKmPrefill();
            }
            // For correction / start-lock: still fetch last km when missing
            if (!this.booking.vehicleLastKmStand && this.booking.vehicle && this.booking.vehicle.id) {
              this.vehicleGetLastKmStand(this.booking.vehicle.id);
            }
        })
        .catch(function (error) {
          
        });
    },
    bookingDelete() {
      if(
        confirm("Vil du slette denne booking?")
      ) {
        axios.get(process.env.VUE_APP_API_ROOT + '/booking.php?mode=delete&id=' + this.booking.id)
          .then(response => {
            this.isDeleted = true;
          })
      }      
    },
    bookingEnd() {
      if(
        confirm("Vil du stoppe denne booking? Så kan andre  booke køretøjet.")
      ) {
        axios.get(process.env.VUE_APP_API_ROOT + '/booking.php?mode=endBooking&id=' + this.booking.id + '&endOriginal=' + this.booking.end)
          .then(response => {
            this.bookingLoad();
          })
      }      
    },
    getKmInputValue(refName) {
      let raw = '';
      if (refName === 'kmStandEnd') {
        raw = this.kmStandEndInput;
      } else if (refName === 'kmStandStart') {
        raw = this.kmStandStartInput;
      } else if (this.$refs[refName]) {
        raw = this.$refs[refName].value;
      }
      return parseKmDigits(raw);
    },
    validateKmStands(startKm, endKm) {
      const assessment = assessKmStands({
        startKm,
        endKm,
        bookingStart: this.booking.start,
        bookingEnd: this.booking.end,
        endPrefillPrefix: this.kmStandEndPrefillPrefix,
        neighbors: this.isKmCorrectionMode ? this.booking.kmNeighbors : null
      });
      if (!assessment.ok) {
        this.kmStandError = assessment.error;
        this.kmStandWarning = '';
        return null;
      }
      this.kmStandError = '';
      this.kmStandWarning = assessment.warnings.join(' ');
      return assessment;
    },
    refreshKmLiveFeedback() {
      const startKm = this.getKmInputValue('kmStandStart');
      const endKm = this.getKmInputValue('kmStandEnd');
      if (startKm <= 0 || endKm <= 0) {
        this.kmStandError = '';
        this.kmStandWarning = '';
        return;
      }
      if (this.kmStandEndPrefillPrefix && String(endKm) === this.kmStandEndPrefillPrefix) {
        this.kmStandError = '';
        this.kmStandWarning = '';
        return;
      }
      const assessment = assessKmStands({
        startKm,
        endKm,
        bookingStart: this.booking.start,
        bookingEnd: this.booking.end,
        endPrefillPrefix: this.kmStandEndPrefillPrefix,
        neighbors: this.isKmCorrectionMode ? this.booking.kmNeighbors : null
      });
      if (!assessment.ok) {
        this.kmStandError = assessment.error;
        this.kmStandWarning = '';
        return;
      }
      this.kmStandError = '';
      this.kmStandWarning = assessment.warnings.join(' ');
    },
    saveKmField(field, km) {
      return axios.get(
        process.env.VUE_APP_API_ROOT + '/distance.php?mode=save&km=' + km + '&field=' + field + '&booking=' + this.booking.id
      );
    },
    saveKmCorrection(startKm, endKm) {
      return axios.get(process.env.VUE_APP_API_ROOT + '/booking-km-correction.php', {
        params: {
          bookingId: this.booking.id,
          kmStandStart: startKm,
          kmStandEnd: endKm
        }
      }).then((response) => {
        const data = response.data || {};
        if (!data.success) {
          const msg = (data.error && data.error.friendly && data.error.friendly.da)
            || 'Kunne ikke rette km-stande.';
          const err = new Error(msg);
          err.responseData = data;
          throw err;
        }
        return data;
      });
    },
    saveKmStands() {
      if (this.kmSaveSubmitting) {
        return;
      }

      const startKm = this.getKmInputValue('kmStandStart');
      const endKm = this.getKmInputValue('kmStandEnd');

      if (this.isKmCorrectionMode && (startKm <= 0 || endKm <= 0)) {
        this.kmStandError = 'Angiv både start- og slut-km.';
        this.buttons.kmSave = 'Gem';
        return;
      }

      const assessment = this.validateKmStands(startKm, endKm);
      if (!assessment) {
        this.buttons.kmSave = 'Gem';
        return;
      }

      const confirmMsg = buildKmConfirmMessage({
        startKm,
        endKm,
        distance: assessment.distance,
        warnings: assessment.warnings
      });
      if (!confirm(confirmMsg)) {
        return;
      }

      this.kmSaveSubmitting = true;
      this.buttons.kmSave = 'Gemmer…';
      this.messagesOnSaves.kmSaved.visible = false;

      const savePromise = this.isKmCorrectionMode
        ? this.saveKmCorrection(startKm, endKm)
        : Promise.all([
            startKm > 0 ? this.saveKmField('kmStandStart', startKm) : null,
            endKm > 0 ? this.saveKmField('kmStandEnd', endKm) : null
          ].filter(Boolean)).then(() => {
            if (startKm > 0) this.booking.kmStandStart = startKm;
            if (endKm > 0) this.booking.kmStandEnd = endKm;
          });

      savePromise
        .then(() => {
          if (endKm > 0) {
            this.kmStandEndPrefillPrefix = '';
          }
          setTimeout(() => {
            this.buttons.kmSave = 'Gem';
            this.kmSaveSubmitting = false;
            this.messagesOnSaves.kmSaved.visible = true;
            this.bookingLoad();
            setTimeout(() => {
              this.messagesOnSaves.kmSaved.visible = false;
            }, 3000);
          }, 400);
        })
        .catch((error) => {
          console.error('Gem km-stand fejlede:', error);
          this.buttons.kmSave = 'Gem';
          this.kmSaveSubmitting = false;
          this.kmStandError = friendlyKmApiError(error, 'Kunne ikke gemme km-stande. Prøv igen.');
        });
    },
    vehicleGetLastKmStand(vehicleId) {
      axios.get(process.env.VUE_APP_API_ROOT + '/distance.php?vehicle=' + vehicleId)
        .then(response => {
          this.booking.vehicleLastKmStand = response.data.lastKmStandEnd;
          if (parseInt(this.booking.kmStandStart, 10) === 0) {
            this.applyStartKmValue();
            this.applyEndKmPrefill();
          }
        })
    },
    formatDate(date) {
      if (!date) return '';
      return date.substring(8, 10) + '. ' + parseInt(date.substring(5, 7)) + '. ' + date.substring(0, 4) + ' kl. ' + date.substring(11, 16);
    },
    formatPeriod(start, end) {
      if (!start || !end) return '';
      const startDate = start.substring(0, 10);
      const endDate = end.substring(0, 10);
      const startTime = start.substring(11, 16);
      const endTime = end.substring(11, 16);
      const dateLabel = (date) =>
        date.substring(8, 10) + '. ' + parseInt(date.substring(5, 7)) + '. ' + date.substring(0, 4);

      if (startDate === endDate) {
        return dateLabel(startDate) + ' kl. ' + startTime + '–' + endTime;
      }

      return dateLabel(startDate) + ' kl. ' + startTime + ' – ' + dateLabel(endDate) + ' kl. ' + endTime;
    },
    onShareUpdated({ isPublic, shareNote }) {
      this.booking.isPublic = isPublic;
      this.booking.shareNote = shareNote;
    }
  },
  computed: {
    currentRoute() {
      return this.$route;
    },
    isOwnAccount() {
      return !!(
        this.booking.id &&
        this.booking.createdBy &&
        this.global.user.account == this.booking.createdBy.account
      );
    },
    isAdmin() {
      return Number(this.global.user.accessLvl) === 1;
    },
    kmCorrectionsList() {
      const list = this.booking.kmCorrections;
      return Array.isArray(list) ? list : [];
    },
    canActivateKmCorrection() {
      return !this.isAdmin
        && this.booking.phase == 100
        && !!this.booking.canCorrectKm;
    },
    isKmCorrectionEditing() {
      return this.canActivateKmCorrection && this.kmCorrectionActive;
    },
    isKmCorrectionMode() {
      return this.isKmCorrectionEditing;
    },
    kmSavedSuccessText() {
      if (this.booking.phase == 100) {
        return 'Km-stande er rettet. Administratorerne er notificeret.';
      }
      return 'Km-stande er gemt. Du kan ændre dem frem til, at din booking slutter.';
    },
    showKmEditor() {
      if (!this.booking.id) return false;
      if (this.isKmCorrectionEditing) return true;
      return this.isOwnAccount && this.booking.phase <= 11;
    },
    showStartKmInfo() {
      return !this.isKmCorrectionEditing
        && parseInt(this.booking.kmStandStart, 10) === 0
        && parseInt(this.booking.vehicleLastKmStand, 10) > 0;
    },
    startKmLocked() {
      if (this.isKmCorrectionEditing || this.startKmUnlocked) {
        return false;
      }
      if (parseInt(this.booking.kmStandStart, 10) > 0) {
        return false;
      }
      return parseInt(this.booking.vehicleLastKmStand, 10) > 0;
    },
    startKmDeviated() {
      if (this.isKmCorrectionEditing || this.startKmLocked) {
        return false;
      }
      const last = parseInt(this.booking.vehicleLastKmStand, 10) || 0;
      if (last <= 0) return false;
      const current = parseKmDigits(this.kmStandStartInput);
      return current > 0 && current !== last && parseInt(this.booking.kmStandStart, 10) === 0;
    },
    liveDrivenKm() {
      const startKm = parseKmDigits(this.kmStandStartInput);
      const endKm = parseKmDigits(this.kmStandEndInput);
      if (startKm <= 0 || endKm <= 0) {
        return null;
      }
      if (this.kmStandEndPrefillPrefix && String(endKm) === this.kmStandEndPrefillPrefix) {
        return null;
      }
      return endKm - startKm;
    },
    calendarBackHref() {
      const date = (this.booking.start || '').substring(0, 10)
        || this.global.navigation.calendarDate
        || '';
      const view = ['day', 'week', 'month'].includes(this.global.navigation.calendarView)
        ? this.global.navigation.calendarView
        : 'day';
      const query = new URLSearchParams();
      if (date) {
        query.set('selectedDate', date);
      }
      query.set('view', view);
      return '/kalender?' + query.toString();
    },
    backHref() {
      if (this.global.navigation.navigatedFrom === 'Calendar') {
        return this.calendarBackHref;
      }
      if (this.global.navigation.navigatedFrom === 'MyBookings') {
        return '/mine-bookinger';
      }
      return null;
    },
    canEditBooking() {
      if (!this.booking.id || this.global.user.account != this.booking.createdBy.account) {
        return false;
      }
      if (this.booking.phase == 20) {
        return true;
      }
      if (
        (this.booking.phase == 10 || this.booking.phase == 11)
        && this.booking.endOriginal == '0000-00-00 00:00:00'
      ) {
        return true;
      }
      return false;
    },
    effectiveStartKm() {
      const savedStart = parseInt(this.booking.kmStandStart, 10) || 0;
      if (savedStart > 0) {
        return savedStart;
      }
      return parseInt(this.booking.vehicleLastKmStand, 10) || 0;
    },
    kmStandEndIsPrefill() {
      return !!this.kmStandEndPrefillPrefix
        && parseInt(this.booking.kmStandEnd, 10) === 0
        && this.kmStandEndInput === this.kmStandEndPrefillPrefix;
    }
  },
  watch: {
    kmStandStartInput() {
      if (!this.isKmCorrectionEditing && parseInt(this.booking.kmStandEnd, 10) === 0) {
        this.applyEndKmPrefill();
      }
      this.refreshKmLiveFeedback();
    },
    kmStandEndInput() {
      this.refreshKmLiveFeedback();
    }
  }
})
</script>

<style scoped>
.booking-back {
  display: flex;
  width: max-content;
  margin-bottom: 1.5rem !important;
}

.table tbody tr:first-child th,
.table tbody tr:first-child td {
  border-top: 1px solid #dbdbdb;
}
.table tbody tr:last-child th,
.table tbody tr:last-child td {
  border-bottom: 1px solid #dbdbdb;
}

/* Nulstil-ikon i km-felter */
.km-reset-icon {
  position: absolute;
  top: 0;
  bottom: 0;
  right: 1.4em; /* leave room for type=number spinners */
  width: 2.25em;
  z-index: 4;
  border: 0;
  background: transparent;
  color: #7a7a7a;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}
.km-reset-icon:hover {
  color: #363636;
}
.control.has-icons-right .input {
  padding-right: 3.6em;
}

.input.is-locked {
  background: #f5f5f5;
  color: #4a4a4a;
  cursor: default;
}

/* Kompakte beskeder på hele booking-siden */
.message {
  font-size: 0.85rem;
}
.message .message-body {
  padding: 0.45rem 0.7rem;
  line-height: 1.35;
}

.km-stands__row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
  gap: 0.5rem 0.55rem;
  align-items: start;
}

.km-stands__field {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.km-stands__field-head {
  display: flex;
  align-items: center;
  gap: 0.2rem;
  margin-bottom: 0.25rem;
  min-height: 1.25rem;
}

.km-stands__field label {
  display: block;
  margin-bottom: 0;
  font-size: 0.7rem;
  font-weight: 600;
  color: #5c5c5c;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.km-info__btn {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: #E8EEF8;
  color: #2F5EB8;
  cursor: pointer;
}

.km-info__btn:hover,
.km-info__btn.is-open {
  background: #D6E0F4;
  color: #244A94;
}

.km-info__tip {
  margin: 0.5rem 0 0;
  padding: 0.45rem 0.55rem;
  border: 0;
  border-radius: 4px;
  background: #E8EEF8;
  color: #244A94;
  font-size: 0.75rem;
  line-height: 1.35;
}

.km-stands__field .control {
  position: relative;
  margin-bottom: 0 !important;
}

.km-stands__action {
  /* Align button with inputs (below shared label row) */
  padding-top: calc(1.25rem + 0.25rem);
  display: flex;
  align-items: flex-start;
}

.km-stands__action .button {
  height: 2.5em;
  min-width: 4.25rem;
  padding-left: 0.9rem;
  padding-right: 0.9rem;
  white-space: nowrap;
}

.km-stands__live {
  margin: 0.55rem 0 0;
  font-size: 0.95rem;
  color: #1a1a1a;
}

.km-stands__live--invalid {
  color: #cc0f35;
}

.km-stands__lock-note {
  margin: 0.3rem 0 0;
  font-size: 0.72rem;
  line-height: 1.35;
  color: #5c5c5c;
}

.km-stands__lock-note--warn {
  color: #946c00;
}

.km-unlock__btn {
  display: inline;
  margin-left: 0.35rem;
  padding: 0;
  border: 0;
  background: none;
  color: #2F5EB8;
  font-size: inherit;
  text-decoration: underline;
  cursor: pointer;
}

.km-stands__closed-note {
  margin: 0.75rem 0 0;
  font-size: 0.8rem;
  color: #5c5c5c;
}

.km-corrections-list {
  margin: 0;
  padding-left: 1.1rem;
  font-size: 0.85rem;
  line-height: 1.45;
}

.km-corrections-list li + li {
  margin-top: 0.35rem;
}

.km-stands__messages {
  margin-top: 0.65rem;
}

.km-stands__messages .message {
  margin: 0;
}

.km-stands__messages .message + .message {
  margin-top: 0.35rem;
}

@media (max-width: 640px) {
  .km-stands__row {
    grid-template-columns: 1fr;
  }
  .km-stands__action {
    padding-top: 0;
  }
  .km-stands__action .button {
    width: 100%;
  }
}
</style>
